<div class="onboarding-page">
    <div class="onboarding-card">
        <!-- Brand header with dark logo badge so the white-element logo reads well -->
        <div class="onboarding-header">
            <div class="logo-badge">
                <img
                    src="./../../assets/icons/bp-color-logo.png"
                    alt="Blumepress"
                    class="brand-logo"
                />
            </div>
        </div>

        <!-- Onboarding form (lead has not submitted yet) -->
        <ng-container *ngIf="status !== 'submitted'">
            <h1 class="onboarding-title">Let's set up your pressroom</h1>
            <p class="onboarding-subtitle">
                Tell us a little about your needs and our team will get your
                account ready.
            </p>

            <div class="alert-banner" *ngIf="errorMessage" role="alert">
                <mat-icon class="alert-icon">error_outline</mat-icon>
                <span>{{ errorMessage }}</span>
            </div>

            <form
                class="onboarding-form"
                [formGroup]="onboardingForm"
                (ngSubmit)="submit()"
                novalidate
            >
                <div class="field-grid">
                    <mat-form-field appearance="outline">
                        <mat-label>First name</mat-label>
                        <mat-icon matPrefix>person</mat-icon>
                        <input
                            matInput
                            formControlName="firstName"
                            autocomplete="given-name"
                            required
                        />
                        <mat-error
                            *ngIf="
                                onboardingForm
                                    .get('firstName')
                                    ?.hasError('required')
                            "
                        >
                            First name is
                            <strong>required</strong>
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Last name</mat-label>
                        <mat-icon matPrefix>person</mat-icon>
                        <input
                            matInput
                            formControlName="lastName"
                            autocomplete="family-name"
                            required
                        />
                        <mat-error
                            *ngIf="
                                onboardingForm
                                    .get('lastName')
                                    ?.hasError('required')
                            "
                        >
                            Last name is
                            <strong>required</strong>
                        </mat-error>
                    </mat-form-field>
                </div>

                <mat-form-field class="full-width" appearance="outline">
                    <mat-label>Organization</mat-label>
                    <mat-icon matPrefix>business</mat-icon>
                    <input
                        matInput
                        formControlName="organization"
                        autocomplete="organization"
                        required
                    />
                    <mat-error
                        *ngIf="
                            onboardingForm
                                .get('organization')
                                ?.hasError('required')
                        "
                    >
                        Organization is
                        <strong>required</strong>
                    </mat-error>
                </mat-form-field>

                <div class="field-grid">
                    <mat-form-field appearance="outline">
                        <mat-label>Pressrooms needed</mat-label>
                        <mat-icon matPrefix>dashboard</mat-icon>
                        <mat-select
                            formControlName="numberOfPressrooms"
                            required
                        >
                            <mat-option
                                *ngFor="let opt of pressroomCountOptions"
                                [value]="opt"
                            >
                                {{ opt }}
                            </mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                onboardingForm
                                    .get('numberOfPressrooms')
                                    ?.hasError('required')
                            "
                        >
                            Please select an option
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Pressroom language</mat-label>
                        <mat-icon matPrefix>language</mat-icon>
                        <mat-select
                            formControlName="pressroomLanguage"
                            required
                        >
                            <mat-option
                                *ngFor="let lang of languageOptions"
                                [value]="lang"
                            >
                                {{ lang }}
                            </mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                onboardingForm
                                    .get('pressroomLanguage')
                                    ?.hasError('required')
                            "
                        >
                            Please select a language
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="field-grid">
                    <mat-form-field appearance="outline">
                        <mat-label>
                            Alternate contact email (optional)
                        </mat-label>
                        <mat-icon matPrefix>alternate_email</mat-icon>
                        <input
                            matInput
                            type="email"
                            formControlName="alternateEmail"
                            autocomplete="email"
                            placeholder="name@company.com"
                        />
                        <mat-error
                            *ngIf="
                                onboardingForm
                                    .get('alternateEmail')
                                    ?.hasError('email')
                            "
                        >
                            Please enter a valid email address
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Contact number (optional)</mat-label>
                        <mat-icon matPrefix>call</mat-icon>
                        <input
                            matInput
                            type="tel"
                            formControlName="phone"
                            autocomplete="tel"
                            placeholder="+1 555 123 4567"
                        />
                    </mat-form-field>
                </div>

                <mat-form-field class="full-width" appearance="outline">
                    <mat-label>Website (optional)</mat-label>
                    <mat-icon matPrefix>public</mat-icon>
                    <input
                        matInput
                        formControlName="website"
                        placeholder="example.com"
                    />
                </mat-form-field>

                <mat-form-field class="full-width" appearance="outline">
                    <mat-label>
                        Anything else we should know? (optional)
                    </mat-label>
                    <textarea
                        matInput
                        formControlName="message"
                        rows="3"
                        maxlength="2000"
                    ></textarea>
                </mat-form-field>

                <button
                    mat-flat-button
                    color="primary"
                    class="submit-btn"
                    type="submit"
                    [disabled]="isloading"
                >
                    <span *ngIf="!isloading">Submit details</span>
                    <mat-spinner *ngIf="isloading" diameter="22"></mat-spinner>
                </button>
            </form>
        </ng-container>

        <!-- Thank-you message (lead already submitted) -->
        <ng-container *ngIf="status === 'submitted'">
            <div class="success-state">
                <div class="success-icon-wrap">
                    <mat-icon class="success-icon">check</mat-icon>
                </div>
                <h1 class="onboarding-title">
                    Thank you{{ lead?.firstName ? ', ' + lead.firstName : '' }}!
                </h1>
                <p class="onboarding-subtitle">
                    We've received your details. One of our sales
                    representatives will reach out to you shortly to set up your
                    account.
                </p>
                <button
                    mat-stroked-button
                    color="primary"
                    class="back-btn"
                    (click)="goToLogin()"
                >
                    Back to sign in
                </button>
            </div>
        </ng-container>

        <p class="onboarding-footer">
            &copy; {{ currentYear }} Blumepress. All rights reserved.
        </p>
    </div>
</div>
